import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { GOLD, SILVER, GREEN, RED, CARD_BG, BORDER, MUTED, CHART_COLORS, METAL_TYPES } from '../constants';
import { fmt, fmtDec, today, monthKey, weekKey, getLast30Days, sumField } from '../utils';
import { KPICard, ChartCard, SectionTitle, TransactionTable, EmptyState } from './UI';

export default function Dashboard({ transactions, metalPrices }) {
  const todayStr  = today();
  const todayTx   = transactions.filter((t) => t.date === todayStr);
  const weekTx    = transactions.filter((t) => weekKey(t.date) === weekKey(todayStr));
  const monthTx   = transactions.filter((t) => monthKey(t.date) === monthKey(todayStr));

  const todaySales  = sumField(todayTx, 'Sale');
  const todayBuys   = sumField(todayTx, 'Purchase');
  const weekSales   = sumField(weekTx, 'Sale');
  const monthSales  = sumField(monthTx, 'Sale');
  const monthBuys   = sumField(monthTx, 'Purchase');
  const monthMargin = monthSales - monthBuys;

  const retailSalesMo  = sumField(monthTx, 'Sale', 'Retail');
  const wholeSalesMo   = sumField(monthTx, 'Sale', 'Wholesale');
  const retailBuysMo   = sumField(monthTx, 'Purchase', 'Retail');
  const wholeBuysMo    = sumField(monthTx, 'Purchase', 'Wholesale');

  const last30 = getLast30Days(transactions);

  // Channel pie
  const pieData = [
    { name: 'Retail Sales',      value: retailSalesMo },
    { name: 'Wholesale Sales',   value: wholeSalesMo },
    { name: 'Retail Buys',       value: retailBuysMo },
    { name: 'Wholesale Buys',    value: wholeBuysMo },
  ].filter((d) => d.value > 0);

  // Category bar
  const catMap = {};
  monthTx.forEach((t) => {
    const k = t.category || 'Other';
    if (!catMap[k]) catMap[k] = { cat: k.split(' ')[0], sell: 0, buy: 0 };
    if (t.direction === 'Sale') catMap[k].sell += t.totalAmount || 0;
    else catMap[k].buy += t.totalAmount || 0;
  });
  const catData = Object.values(catMap).sort((a, b) => (b.sell + b.buy) - (a.sell + a.buy)).slice(0, 8);

  // Metal volume bar (weight)
  const metalWeight = {};
  METAL_TYPES.forEach((m) => { metalWeight[m] = { buy: 0, sell: 0 }; });
  transactions.forEach((t) => {
    if (t.metal && metalWeight[t.metal] && t.weight) {
      if (t.direction === 'Purchase') metalWeight[t.metal].buy += (t.weight || 0) * (t.qty || 1);
      else metalWeight[t.metal].sell += (t.weight || 0) * (t.qty || 1);
    }
  });
  const metalData = METAL_TYPES.map((m) => ({ metal: m, ...metalWeight[m] }));

  const tooltipStyle = { contentStyle: { background: CARD_BG, border: `1px solid ${BORDER}`, fontSize: 11, borderRadius: 6 } };

  return (
    <div>
      <SectionTitle title="Operations Dashboard" sub={`${todayStr} · Johns Creek, GA · All-time ${transactions.length} transactions`} />

      {/* KPI ROW 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        <KPICard label="Today Sales"     value={fmt(todaySales)}  sub={`${todayTx.filter(t=>t.direction==='Sale').length} transactions`} color={GREEN} />
        <KPICard label="Today Purchases" value={fmt(todayBuys)}   sub={`${todayTx.filter(t=>t.direction==='Purchase').length} transactions`} color={RED} />
        <KPICard label="Today Net"       value={fmt(todaySales - todayBuys)} sub="gross spread" color={GOLD} />
        <KPICard label="Week Sales"      value={fmt(weekSales)}   sub={weekTx.length + ' transactions'} color={GREEN} />
      </div>

      {/* KPI ROW 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <KPICard label="Month Revenue"    value={fmt(monthSales)}   sub={monthKey(todayStr)} color={GOLD} />
        <KPICard label="Month Purchases"  value={fmt(monthBuys)}    sub={monthKey(todayStr)} color={RED} />
        <KPICard label="Month Margin"     value={fmt(monthMargin)}  sub={monthSales > 0 ? `${((monthMargin/monthSales)*100).toFixed(1)}% margin` : '—'} color={monthMargin >= 0 ? GREEN : RED} />
        <KPICard label="Retail / Wholesale" value={`${fmt(retailSalesMo)} / ${fmt(wholeSalesMo)}`} sub="sales split this month" color={SILVER} />
      </div>

      {/* CHARTS ROW 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="30-Day Sales vs Purchases">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={last30}>
              <defs>
                <linearGradient id="gS" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GREEN} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gB" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={RED} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={RED} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="label" stroke={MUTED} tick={{ fontSize: 9 }} />
              <YAxis stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Area type="monotone" dataKey="sales" name="Sales" stroke={GREEN} fill="url(#gS)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="purchases" name="Purchases" stroke={RED} fill="url(#gB)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={`Channel Mix — ${monthKey(todayStr)}`}>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                  {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No data this month" />}
        </ChartCard>
      </div>

      {/* CHARTS ROW 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <ChartCard title={`Category Breakdown — ${monthKey(todayStr)}`}>
          {catData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={catData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} horizontal={false} />
                <XAxis type="number" stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="cat" stroke={MUTED} tick={{ fontSize: 9 }} width={55} />
                <Tooltip {...tooltipStyle} formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="sell" name="Sales" fill={GREEN} radius={[0,3,3,0]} />
                <Bar dataKey="buy"  name="Buys"  fill={RED}   radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No category data" />}
        </ChartCard>

        <ChartCard title="Cumulative Metal Weight (oz) — All Time">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={metalData}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="metal" stroke={MUTED} tick={{ fontSize: 10 }} />
              <YAxis stroke={MUTED} tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}oz`} />
              <Tooltip {...tooltipStyle} formatter={(v) => `${v.toFixed(3)} oz`} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="buy"  name="Purchased" fill={RED}   radius={[3,3,0,0]} />
              <Bar dataKey="sell" name="Sold"      fill={GREEN} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* RECENT */}
      <SectionTitle title="Recent Transactions" sub="Last 15 entries" />
      <TransactionTable rows={transactions.slice(0, 15)} compact />
    </div>
  );
}
